import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../config';
import CualappLogo from './CualappLogo';

/**
 * Flujo de recuperacion de contrasena por OTP (SMS / WhatsApp via Twilio).
 *
 * Stages:
 *   0: Mensaje de servicio deshabilitado (pre-LLC) — instruye contactar soporte.
 *   1: Email + canal -> POST /start
 *   2: Codigo OTP -> POST /verify
 *   3: Nueva contrasena -> POST /reset
 *   ok: Exito.
 */
const ForgotPasswordPage = () => {
  const [status, setStatus] = useState(null);
  const [stage, setStage] = useState(1);
  const [email, setEmail] = useState('');
  const [channel, setChannel] = useState('whatsapp');
  const [code, setCode] = useState('');
  const [resetToken, setResetToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [info, setInfo] = useState(null);
  const [cooldownInfo, setCooldownInfo] = useState(null);

  useEffect(() => {
    axios.get(`${API}/auth/forgot-password/status`)
      .then(r => {
        setStatus(r.data);
        if (r.data.default_channel) setChannel(r.data.default_channel);
      })
      .catch(() => setStatus({ enabled: false }));
  }, []);

  const goBack = () => { window.location.href = '/'; };

  const startReset = async (e) => {
    e?.preventDefault?.();
    setError(null); setInfo(null); setCooldownInfo(null); setLoading(true);
    try {
      const r = await axios.post(`${API}/auth/forgot-password/start`, { email, channel });
      if (r.data.ok === false) {
        // Bloqueado por cooldown o monthly_limit
        setCooldownInfo(r.data);
      } else {
        setInfo(r.data.message || 'Codigo enviado');
        setStage(2);
      }
    } catch (err) {
      const detail = err.response?.data?.detail || 'Error enviando codigo';
      setError(humanError(detail));
    } finally { setLoading(false); }
  };

  const verifyCode = async (e) => {
    e?.preventDefault?.();
    setError(null); setLoading(true);
    try {
      const r = await axios.post(`${API}/auth/forgot-password/verify`, { email, code });
      setResetToken(r.data.reset_token);
      setStage(3);
    } catch (err) {
      const detail = err.response?.data?.detail || 'Codigo invalido';
      setError(humanError(detail));
    } finally { setLoading(false); }
  };

  const doReset = async (e) => {
    e?.preventDefault?.();
    setError(null);
    if (newPassword.length < 8) { setError('La contrasena debe tener al menos 8 caracteres.'); return; }
    if (newPassword !== confirmPassword) { setError('Las contrasenas no coinciden.'); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password/reset`, { reset_token: resetToken, new_password: newPassword });
      setStage('ok');
    } catch (err) {
      const detail = err.response?.data?.detail || 'Error cambiando contrasena';
      setError(humanError(detail));
    } finally { setLoading(false); }
  };

  // Servicio deshabilitado (pre-LLC): fallback claro al usuario
  if (status && status.enabled === false) {
    return (
      <Shell>
        <Card title="Recuperar contrasena" testid="forgot-password-disabled">
          <p className="text-sm text-gray-700 mb-3">
            La recuperacion automatica por SMS/WhatsApp se activara muy pronto.
          </p>
          <p className="text-sm text-gray-700 mb-3">
            Mientras tanto, si olvidaste tu contrasena, escribe a:
          </p>
          <a href="mailto:soporte@cualapp.com" className="inline-block px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-semibold hover:bg-purple-700">
            soporte@cualapp.com
          </a>
          <button onClick={goBack} className="mt-4 block text-xs text-gray-500 hover:underline" data-testid="forgot-back-disabled">Volver al inicio</button>
        </Card>
      </Shell>
    );
  }

  return (
    <Shell>
      <Card title="Recuperar contrasena" testid="forgot-password-page">
        {error && <div className="mb-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm p-3" data-testid="forgot-error">{error}</div>}
        {info && <div className="mb-3 rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-800 text-sm p-3" data-testid="forgot-info">{info}</div>}

        {cooldownInfo && <CooldownCard data={cooldownInfo} email={email} onExpressDone={() => { setCooldownInfo(null); startReset(); }} />}

        {!cooldownInfo && stage === 1 && (
          <form onSubmit={startReset} className="space-y-4" data-testid="forgot-stage-1">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Correo electronico</label>
              <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                data-testid="forgot-email"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500" />
            </div>
            {status && status.channels && status.channels.length > 1 && (
              <div>
                <p className="block text-xs font-medium text-gray-700 mb-2">Como quieres recibir el codigo</p>
                <div className="space-y-1">
                  {status.channels.includes('whatsapp') && (
                    <label className="flex items-center gap-2 text-sm">
                      <input type="radio" name="channel" value="whatsapp" checked={channel === 'whatsapp'} onChange={() => setChannel('whatsapp')} data-testid="forgot-channel-whatsapp" />
                      WhatsApp (recomendado)
                    </label>
                  )}
                  {status.channels.includes('sms') && (
                    <label className="flex items-center gap-2 text-sm">
                      <input type="radio" name="channel" value="sms" checked={channel === 'sms'} onChange={() => setChannel('sms')} data-testid="forgot-channel-sms" />
                      SMS
                    </label>
                  )}
                </div>
              </div>
            )}
            <button type="submit" disabled={loading} data-testid="forgot-send-btn"
              className="w-full px-4 py-2.5 bg-purple-600 text-white text-sm font-semibold rounded-lg hover:bg-purple-700 disabled:opacity-50">
              {loading ? 'Enviando...' : 'Enviar codigo'}
            </button>
            <button type="button" onClick={goBack} className="w-full text-xs text-gray-500 hover:underline" data-testid="forgot-back-1">Volver al inicio</button>
          </form>
        )}

        {!cooldownInfo && stage === 2 && (
          <form onSubmit={verifyCode} className="space-y-4" data-testid="forgot-stage-2">
            <p className="text-xs text-gray-600">Ingresa el codigo de 6 digitos que recibiste.</p>
            <input type="text" required maxLength={10} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
              data-testid="forgot-code"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-lg text-center tracking-widest font-mono focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              placeholder="123456" />
            <button type="submit" disabled={loading || !code} data-testid="forgot-verify-btn"
              className="w-full px-4 py-2.5 bg-purple-600 text-white text-sm font-semibold rounded-lg hover:bg-purple-700 disabled:opacity-50">
              {loading ? 'Verificando...' : 'Verificar codigo'}
            </button>
            <button type="button" onClick={() => { setStage(1); setCode(''); setInfo(null); }} className="w-full text-xs text-gray-500 hover:underline" data-testid="forgot-back-2">Cambiar correo o canal</button>
          </form>
        )}

        {!cooldownInfo && stage === 3 && (
          <form onSubmit={doReset} className="space-y-4" data-testid="forgot-stage-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Nueva contrasena</label>
              <input type="password" required minLength={8} value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
                data-testid="forgot-new-password"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Confirmar nueva contrasena</label>
              <input type="password" required minLength={8} value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)}
                data-testid="forgot-confirm-password"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500" />
            </div>
            <button type="submit" disabled={loading} data-testid="forgot-reset-btn"
              className="w-full px-4 py-2.5 bg-purple-600 text-white text-sm font-semibold rounded-lg hover:bg-purple-700 disabled:opacity-50">
              {loading ? 'Guardando...' : 'Guardar nueva contrasena'}
            </button>
          </form>
        )}

        {stage === 'ok' && (
          <div className="space-y-4" data-testid="forgot-ok">
            <div className="rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-800 text-sm p-3">
              Contrasena actualizada. Ya puedes iniciar sesion con tu nueva clave.
            </div>
            <button onClick={goBack} className="w-full px-4 py-2.5 bg-purple-600 text-white text-sm font-semibold rounded-lg hover:bg-purple-700" data-testid="forgot-go-login">
              Ir a iniciar sesion
            </button>
          </div>
        )}
      </Card>
    </Shell>
  );
};

const CooldownCard = ({ data, email, onExpressDone }) => {
  const [expressLoading, setExpressLoading] = useState(false);
  const [expressErr, setExpressErr] = useState(null);

  const handleExpress = async () => {
    setExpressLoading(true); setExpressErr(null);
    try {
      await axios.post(`${API}/auth/forgot-password/express`, { email });
      onExpressDone();
    } catch (err) {
      const detail = err.response?.data?.detail || 'Error procesando pago';
      setExpressErr(humanError(detail));
    } finally { setExpressLoading(false); }
  };

  if (data.reason === 'monthly_limit_exceeded') {
    return (
      <div className="rounded-lg bg-amber-50 border border-amber-200 p-4 text-sm text-amber-900 space-y-2" data-testid="cooldown-monthly">
        <p className="font-semibold">Alcanzaste el limite mensual</p>
        <p>Tu tier <strong>{data.tier}</strong> permite {data.monthly_limit} recuperaciones por mes. Has usado {data.attempts_this_month}.</p>
        <p className="text-xs">Para mas recuperaciones, considera subir de tier en tu Celula o contacta a <a href="mailto:soporte@cualapp.com" className="underline">soporte@cualapp.com</a>.</p>
      </div>
    );
  }

  return (
    <div className="rounded-lg bg-amber-50 border border-amber-200 p-4 text-sm text-amber-900 space-y-3" data-testid="cooldown-active">
      <p className="font-semibold">En espera de cooldown</p>
      <p>Por seguridad debes esperar antes de solicitar otro codigo.</p>
      {data.cooldown_seconds && (
        <p className="text-xs">Tiempo restante: <strong><Countdown seconds={data.cooldown_seconds} /></strong></p>
      )}
      <p className="text-xs">Has usado {data.attempts_this_month} de {data.monthly_limit} recuperaciones este mes.</p>

      <div className="border-t border-amber-200 pt-3 mt-2">
        <p className="font-semibold text-purple-900">⚡ Recuperacion Express</p>
        <p className="text-xs text-gray-700 mt-1">Salta el cooldown pagando 50 Cc. Acceso inmediato.</p>
        {expressErr && <p className="text-xs text-red-600 mt-1">{expressErr}</p>}
        <button onClick={handleExpress} disabled={expressLoading} data-testid="express-pay-btn"
          className="mt-2 px-3 py-1.5 bg-purple-600 text-white text-xs font-semibold rounded hover:bg-purple-700 disabled:opacity-50">
          {expressLoading ? 'Procesando...' : 'Pagar 50 Cc y continuar'}
        </button>
      </div>
    </div>
  );
};

const Countdown = ({ seconds }) => {
  const [remaining, setRemaining] = useState(seconds);
  useEffect(() => {
    const id = setInterval(() => setRemaining(r => Math.max(0, r - 1)), 1000);
    return () => clearInterval(id);
  }, []);
  const h = Math.floor(remaining / 3600);
  const m = Math.floor((remaining % 3600) / 60);
  const s = remaining % 60;
  if (h > 0) return <>{h}h {m}m {s}s</>;
  if (m > 0) return <>{m}m {s}s</>;
  return <>{s}s</>;
};

const Shell = ({ children }) => (
  <div className="min-h-screen bg-gradient-to-br from-purple-50 to-blue-50 flex items-center justify-center p-4">
    <div className="w-full max-w-md">
      <div className="flex justify-center mb-6">
        <CualappLogo size={56} withWordmark />
      </div>
      {children}
    </div>
  </div>
);

const Card = ({ title, children, testid }) => (
  <div className="bg-white rounded-2xl shadow-xl p-8" data-testid={testid}>
    <h1 className="text-xl font-bold text-gray-900 mb-4">{title}</h1>
    {children}
  </div>
);

const ERROR_MAP = {
  otp_service_disabled: 'El servicio de recuperacion automatica aun no esta disponible. Escribe a soporte@cualapp.com.',
  phone_not_registered: 'No hay un telefono registrado en tu cuenta. Contacta soporte@cualapp.com.',
  invalid_phone_format: 'El formato del telefono registrado no es valido.',
  whatsapp_not_available_for_number: 'WhatsApp no esta disponible para este numero. Intenta SMS.',
  twilio_rate_limit: 'Demasiados intentos en poco tiempo. Espera unos minutos.',
  twilio_send_error: 'No se pudo enviar el codigo. Intenta de nuevo en unos minutos.',
  twilio_verify_error: 'No se pudo verificar el codigo. Intenta de nuevo.',
  otp_max_attempts: 'Demasiados intentos fallidos. Solicita un codigo nuevo.',
  invalid_code: 'Codigo invalido o expirado.',
  reset_token_expired: 'La sesion de recuperacion expiro. Inicia el flujo nuevamente.',
  reset_token_invalid: 'Token de recuperacion invalido.',
  reset_token_already_used: 'Este enlace de recuperacion ya fue usado.',
  same_as_current_password: 'La nueva contrasena debe ser diferente a la actual.',
  monthly_limit_exceeded: 'Alcanzaste el limite mensual de recuperaciones.',
  insufficient_cualcoin: 'No tienes suficientes Cc para Recuperacion Express.',
  channel_not_supported: 'Ese canal no esta disponible ahora mismo.',
  user_not_found: 'No encontramos esa cuenta.',
};

function humanError(detail) {
  return ERROR_MAP[detail] || `Error: ${detail}`;
}

export default ForgotPasswordPage;
